/**
 * <product-finder> — sections/product-finder.liquid
 *
 * 3-step quiz (device -> lifestyle -> budget), one selection per step.
 * Finishing the last step redirects to the collection page with the chosen
 * tags in the path and the budget as a price filter:
 *
 *   /collections/all-product/device-apple+gaming+lifestyle-gaming
 *     ?filter.v.price.gte=100&filter.v.price.lte=500
 */
(function () {
  if (customElements.get("product-finder")) return;

  const STEP_KEYS = ["device", "lifestyle", "budget"];


  function handleize(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/['"’]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  class ProductFinder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("[data-pfx-form]");
      this.steps = Array.from(this.querySelectorAll("[data-pfx-step]"));
      this.progressSteps = Array.from(this.closest(".pfx")?.querySelectorAll("[data-pfx-progress-step]") || []);
      this.nextBtn = this.querySelector("[data-pfx-next]");
      this.prevBtn = this.querySelector("[data-pfx-prev]");
      this.redirectNotice = this.querySelector("[data-pfx-redirect-notice]");

      this.baseUrl = this.dataset.baseUrl || "/collections/all";
      this.sortBy = this.dataset.sortBy || "";

      if (!this.form || !this.steps.length) return;

      this.currentStep = 0;
      this.selection = {};

      this.steps.forEach((step, stepIndex) => {
        const options = Array.from(step.querySelectorAll("[data-pfx-option]"));
        options.forEach((option) => {
          option.addEventListener("click", () => this.selectOption(stepIndex, option));
        });
      });

      this.nextBtn.addEventListener("click", () => this.goNext());
      this.prevBtn.addEventListener("click", () => this.goPrev());

      this.updateProgress();
    }

    selectOption(stepIndex, option) {
      const step = this.steps[stepIndex];
      step.querySelectorAll("[data-pfx-option]").forEach((el) => {
        el.setAttribute("aria-checked", String(el === option));
      });

      const key = STEP_KEYS[stepIndex];

      if (key === "lifestyle") {
        this.selection[key] = (option.dataset.tags || "")
          .split("||")
          .map((tag) => tag.trim())
          .filter(Boolean);
      } else if (key === "budget") {
        this.selection[key] = {
          min: option.dataset.minPrice || "",
          max: option.dataset.maxPrice || "",
        };
      } else {
        this.selection[key] = option.dataset.tag;
      }

      this.nextBtn.disabled = false;
      this.centerInViewport();
    }

   
    centerInViewport() {
      const section = this.closest(".pfx") || this;
      const activeStep = this.steps[this.currentStep];
      const target = section.offsetHeight > window.innerHeight && activeStep ? activeStep : section;

      const rect = target.getBoundingClientRect();
      const delta = rect.top + rect.height / 2 - window.innerHeight / 2;
      if (Math.abs(delta) < 2) return;

      window.scrollBy({
        top: delta,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    }

    goNext() {
      if (this.currentStep === this.steps.length - 1) {
        this.submit();
        return;
      }

      this.currentStep += 1;
      this.renderStep();
    }

    goPrev() {
      if (this.currentStep === 0) return;
      this.currentStep -= 1;
      this.renderStep();
    }

    renderStep() {
      this.steps.forEach((step, i) => {
        const isActive = i === this.currentStep;
        step.classList.toggle("is-active", isActive);
        step.hidden = !isActive;
      });

      this.prevBtn.hidden = this.currentStep === 0;

      const stepKey = STEP_KEYS[this.currentStep];
      this.nextBtn.disabled = !this.selection[stepKey];

      const isLastStep = this.currentStep === this.steps.length - 1;
      this.nextBtn.textContent = isLastStep
        ? this.nextBtn.dataset.finishLabel || this.nextBtn.textContent
        : this.nextBtn.dataset.nextLabel || this.nextBtn.textContent;

      this.updateProgress();

    
      const group = this.steps[this.currentStep].querySelector('[role="radiogroup"]');
      if (group) {
        group.setAttribute("tabindex", "-1");
        group.focus({ preventScroll: true });
      }

      this.centerInViewport();
    }

    updateProgress() {
      this.progressSteps.forEach((el, i) => {
        el.classList.toggle("is-active", i === this.currentStep);
        el.classList.toggle("is-done", i < this.currentStep);
        if (i === this.currentStep) {
          el.setAttribute("aria-current", "step");
        } else {
          el.removeAttribute("aria-current");
        }
      });

      this.scrollProgressIntoView();
    }

    /* the active step visible */
    scrollProgressIntoView() {
      const active = this.progressSteps[this.currentStep];
      const strip = active?.parentElement;
      if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;

      const left = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
      strip.scrollTo({
        left: Math.max(0, Math.min(left, strip.scrollWidth - strip.clientWidth)),
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    }

    /** output -> /collections/<handle>/<tag>+<tag>?<price & sort params> */
    buildUrl() {
      const tags = [];
      if (this.selection.device) tags.push(this.selection.device);
      (this.selection.lifestyle || []).forEach((tag) => tags.push(tag));

      const tagPath = tags
        .map(handleize)
        .filter(Boolean)
        .filter((tag, i, all) => all.indexOf(tag) === i)
        .join("+");

      const basePath = this.baseUrl.replace(/\/+$/, "");
      const url = new URL(tagPath ? basePath + "/" + tagPath : basePath, window.location.origin);
      const params = new URLSearchParams();

      if (this.sortBy) params.set("sort_by", this.sortBy);

      const budget = this.selection.budget;
      if (budget) {
        if (budget.min) params.set("filter.v.price.gte", budget.min);
        if (budget.max) params.set("filter.v.price.lte", budget.max);
      }

      url.search = params.toString();
      return url.toString();
    }

    submit() {
     
      this.nextBtn.disabled = true;
      const loadingLabel = this.nextBtn.dataset.loadingLabel;
      if (loadingLabel) this.nextBtn.textContent = loadingLabel;
      if (this.redirectNotice) this.redirectNotice.hidden = false;

      window.location.assign(this.buildUrl());
    }
  }

  customElements.define("product-finder", ProductFinder);
})();
